import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { adminApi } from '@/api/endpoints'
import { Avatar, ChevronIcon, UsersIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { PageHeader } from '@/components/ui/Page'
import { StatusPill } from '@/components/ui/StatusPill'
import { ManagePlanModal } from '@/features/users/SubscriptionPanel'
import { cn, formatDate, relativeTime } from '@/lib/utils'
import type { UserFilter, UserRow } from '@/types/api'

const FILTERS: Array<{ value: UserFilter; label: string }> = [
  { value: 'all', label: 'Everyone' },
  { value: 'captcha', label: 'CAPTCHA' },
  { value: 'needs_you', label: 'Needs user' },
  { value: 'failed', label: 'Has failures' },
  { value: 'on_plan', label: 'On a plan' },
  { value: 'no_plan', label: 'No plan' },
  { value: 'incomplete', label: 'Onboarding not done' },
  { value: 'admins', label: 'Admins' },
]
const PAGE_SIZE = 25

/**
 * Every account: when it registered, how far its profile got, the plan it is
 * on, and what its applications are doing.
 *
 * The filter and the search live in the address, so a view can be linked or
 * come back after opening a user. Rows are a table from md up and cards on a
 * phone; the same numbers in the same order on both.
 */
export function UsersPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const filter = (FILTERS.find((option) => option.value === params.get('filter'))?.value ?? 'all') as UserFilter
  const search = params.get('search') ?? ''
  const page = Math.max(1, Number(params.get('page')) || 1)

  // The box edits freely; the address (and so the query) follows a moment later.
  const [draft, setDraft] = useState(search)
  useEffect(() => setDraft(search), [search])
  useEffect(() => {
    if (draft.trim() === search) return
    const timer = window.setTimeout(() => update({ search: draft.trim() || null, page: null }), 350)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  function update(changes: Record<string, string | null>) {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key)
      else next.set(key, value)
    }
    setParams(next, { replace: true })
  }

  const users = useQuery({
    queryKey: ['users', { filter, search, page }],
    queryFn: () => adminApi.users({ filter, search: search || undefined, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    // Live counts ride on these rows.
    refetchInterval: 15_000,
  })
  const rows = users.data?.data ?? []
  const info = users.data?.pageInfo
  const [planFor, setPlanFor] = useState<UserRow | null>(null)

  return (
    <>
      <PageHeader
        title="Users"
        description="Who registered, how complete their profile is, the plan they are on and what they applied to. Open a user for everything else."
        actions={info ? <span className="num text-[13px] text-ink-muted">{info.totalItems} {info.totalItems === 1 ? 'account' : 'accounts'}</span> : undefined}
      />

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="hscroll -mx-4 gap-1.5 px-4 sm:mx-0 sm:px-0" role="radiogroup" aria-label="Show">
          {FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={filter === option.value}
              onClick={() => update({ filter: option.value === 'all' ? null : option.value, page: null })}
              className={cn(
                'h-9 shrink-0 rounded-pill border px-3.5 text-[13px] font-medium transition-colors',
                filter === option.value ? 'border-ink bg-ink text-oncolor' : 'border-line bg-surface text-ink-soft hover:bg-surface-muted',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          type="search"
          placeholder="Filter by name or email"
          aria-label="Filter by name or email"
          className="field-box h-9 py-0 text-[14px] lg:ml-auto lg:w-[280px]"
        />
      </div>

      <section className={cn('card overflow-hidden transition-opacity', users.isPlaceholderData && 'opacity-60')}>
        {users.isLoading ? (
          <div className="space-y-3 p-5">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        ) : users.isError ? (
          <ErrorState title="Users could not be loaded" onRetry={() => void users.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<UsersIcon className="h-7 w-7" />}
            title={search ? `Nobody matches "${search}"` : 'Nobody here'}
            description={filter === 'captcha' ? 'No user has a check waiting or an application a CAPTCHA stopped.' : 'Try another filter.'}
          />
        ) : (
          <>
            {/* md+: a table */}
            <div className="hidden md:block">
              <table className="w-full table-fixed text-left">
                <thead>
                  <tr className="border-b border-line text-[11.5px] font-semibold uppercase tracking-[0.08em] text-ink-muted">
                    <th className="px-5 py-2.5 font-semibold">User</th>
                    <th className="w-[150px] px-2 py-2.5 font-semibold">Profile</th>
                    <th className="w-[150px] px-2 py-2.5 font-semibold">Plan</th>
                    <th className="w-[100px] px-2 py-2.5 text-right font-semibold">Applied</th>
                    <th className="w-[104px] whitespace-nowrap px-2 py-2.5 text-right font-semibold">Needs user</th>
                    <th className="hidden w-[140px] px-2 py-2.5 font-semibold xl:table-cell">CAPTCHA</th>
                    <th className="hidden w-[120px] px-2 py-2.5 font-semibold lg:table-cell">Last seen</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((user) => (
                    <tr key={user.id} onClick={() => navigate(`/users/${user.id}`)} className="group cursor-pointer transition-colors hover:bg-surface-muted">
                      <td className="px-5 py-3">
                        <Link to={`/users/${user.id}`} onClick={(event) => event.stopPropagation()} className="flex min-w-0 items-center gap-3">
                          <Avatar name={user.fullName || user.email} />
                          <span className="min-w-0">
                            <span className="flex min-w-0 items-center gap-2">
                              <span className="truncate text-[14px] font-medium text-ink">{user.fullName || user.email}</span>
                              <Tags user={user} />
                            </span>
                            <span className="block truncate text-[12.5px] text-ink-muted">
                              {user.fullName ? `${user.email} · ` : ''}joined {formatDate(user.createdAt)}
                            </span>
                          </span>
                        </Link>
                      </td>
                      <td className="px-2 py-3">
                        <Profile user={user} />
                      </td>
                      <td className="px-2 py-3">
                        <Plan user={user} />
                        <PlanButton user={user} onOpen={setPlanFor} />
                      </td>
                      <td className="px-2 py-3 text-right">
                        <Applied user={user} />
                      </td>
                      <Count value={user.counts.needsYou} tone="text-warning" />
                      <td className="hidden px-2 py-3 xl:table-cell">
                        <Captcha user={user} />
                      </td>
                      <td className="hidden px-2 py-3 text-[12.5px] text-ink-muted lg:table-cell">{user.lastLoginAt ? relativeTime(user.lastLoginAt) : 'Never'}</td>
                      <td className="pr-4 text-right">
                        <ChevronIcon className="inline h-4 w-4 text-ink-faint transition-transform group-hover:translate-x-0.5" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Phones: the same row as a card */}
            <ul className="divide-y divide-line md:hidden">
              {rows.map((user) => (
                <li key={user.id}>
                  <Link to={`/users/${user.id}`} className="block px-4 py-3.5 transition-colors active:bg-surface-muted">
                    <div className="flex items-center gap-3">
                      <Avatar name={user.fullName || user.email} />
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2">
                          <p className="truncate text-[14.5px] font-medium text-ink">{user.fullName || user.email}</p>
                          <Tags user={user} />
                        </div>
                        <p className="truncate text-[12.5px] text-ink-muted">{user.fullName ? user.email : user.lastLoginAt ? `Seen ${relativeTime(user.lastLoginAt)}` : 'Never signed in'}</p>
                      </div>
                      <ChevronIcon className="h-4 w-4 shrink-0 text-ink-faint" />
                    </div>
                    <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 pl-12">
                      <Profile user={user} />
                      <Plan user={user} compact />
                      <span className="text-[12px] text-ink-muted">joined {formatDate(user.createdAt)}</span>
                    </div>
                    {(user.counts.total > 0 || user.liveWaiting > 0) && (
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 pl-12 text-[12.5px] text-ink-muted">
                        <Mini label="applied" value={user.counts.total} tone="text-ink" />
                        <Mini label="sent" value={user.counts.submitted} tone="text-success" />
                        <Mini label="running" value={user.counts.inProgress} tone="text-info" />
                        <Mini label="needs user" value={user.counts.needsYou} tone="text-warning" />
                        <Mini label="failed" value={user.counts.failed} tone="text-danger" />
                        {(user.liveWaiting > 0 || user.counts.captchaStopped > 0) && <Captcha user={user} />}
                      </div>
                    )}
                  </Link>
                  <div className="-mt-1.5 pb-3 pl-16">
                    <PlanButton user={user} onOpen={setPlanFor} />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {info && info.totalPages > 1 && (
        <nav aria-label="Pages" className="mt-4 flex items-center justify-between gap-3">
          <Button variant="secondary" size="sm" disabled={!info.hasPrevious} onClick={() => update({ page: String(page - 1) })}>
            Previous
          </Button>
          <span className="num text-[13px] text-ink-muted">
            {info.page} / {info.totalPages}
          </span>
          <Button variant="secondary" size="sm" disabled={!info.hasNext} onClick={() => update({ page: String(page + 1) })}>
            Next
          </Button>
        </nav>
      )}

      {planFor && <ManagePlanModal open onClose={() => setPlanFor(null)} user={planFor} />}
    </>
  )
}

function Tags({ user }: { user: UserRow }) {
  return (
    <>
      {user.role === 'admin' && (
        <StatusPill tone="brand" size="sm">
          Admin
        </StatusPill>
      )}
      {user.status !== 'active' && (
        <StatusPill tone="neutral" size="sm">
          {user.status.replace(/_/g, ' ')}
        </StatusPill>
      )}
    </>
  )
}

/** Onboarding first (nothing else counts before it is done), then how complete the profile is. */
function Profile({ user }: { user: UserRow }) {
  const percent = user.profilePercent
  const missing = user.profileMissing ?? []
  const title = missing.length ? `Missing: ${missing.join(', ')}` : 'Nothing missing'
  if (!user.onboardingComplete) {
    return (
      <span title={title} className="inline-flex items-center gap-2">
        <StatusPill tone="warning" size="sm">
          Onboarding
        </StatusPill>
        {percent !== null && <span className="num text-[12px] text-ink-muted">{percent}%</span>}
      </span>
    )
  }
  if (percent === null) return <span className="text-[13px] text-ink-faint">–</span>
  const tone = percent >= 80 ? 'bg-success' : percent >= 50 ? 'bg-warning' : 'bg-danger'
  return (
    <span title={title} className="inline-flex items-center gap-2">
      <span className="h-1.5 w-14 overflow-hidden rounded-pill bg-ink/[0.07]" aria-hidden="true">
        <span className={cn('block h-full rounded-pill', tone)} style={{ width: `${percent}%` }} />
      </span>
      <span className="num text-[12.5px] font-medium text-ink">{percent}%</span>
    </span>
  )
}

/** Opens the plan dialog for that row: grant one, or change a running grant's plan and end date. */
function PlanButton({ user, onOpen }: { user: UserRow; onOpen: (user: UserRow) => void }) {
  const paid = user.plan?.source === 'stripe'
  return (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onOpen(user)
      }}
      className="mt-1 block text-[12px] font-medium text-brand hover:underline"
      title={paid ? 'They pay through Stripe: see the plan' : undefined}
    >
      {paid ? 'View plan' : user.plan?.source === 'admin' ? 'Change plan' : 'Assign plan'}
    </button>
  )
}

/** The plan in force: paid or granted, and until when. */
function Plan({ user, compact = false }: { user: UserRow; compact?: boolean }) {
  const plan = user.plan
  if (!plan) return <span className="text-[13px] text-ink-faint">No plan</span>
  const how = plan.source === 'admin' ? 'granted' : 'paid'
  return (
    <span className={cn('min-w-0', compact ? 'inline-flex items-center gap-1.5' : 'flex flex-col')} title={`${how} · ${plan.period} · until ${formatDate(plan.endsAt)}`}>
      <span className="inline-flex items-center gap-1.5">
        <StatusPill tone={plan.source === 'admin' ? 'info' : 'success'} size="sm">
          {plan.name}
        </StatusPill>
        {user.autoApplyEnabled && <span className="text-[11px] font-medium text-brand">Auto</span>}
      </span>
      {!compact && <span className="mt-0.5 whitespace-nowrap text-[11.5px] text-ink-muted">{how} · to {formatDate(plan.endsAt).replace(/,? \d{4}$/, '')}</span>}
    </span>
  )
}

/** Every application, and how many of them were sent. */
function Applied({ user }: { user: UserRow }) {
  if (!user.counts.total) return <span className="num text-[14px] text-ink-faint">–</span>
  return (
    <span className="flex flex-col items-end">
      <span className="num text-[14px] font-medium text-ink">{user.counts.total}</span>
      <span className="num text-[11.5px] text-success">{user.counts.submitted} sent</span>
      {user.lastAppliedAt && <span className="text-[11px] text-ink-muted">last {relativeTime(user.lastAppliedAt)}</span>}
    </span>
  )
}

function Count({ value, tone }: { value: number; tone: string }) {
  return <td className={cn('num px-2 py-3 text-right text-[14px] font-medium', value ? tone : 'text-ink-faint')}>{value || '–'}</td>
}

function Mini({ label, value, tone }: { label: string; value: number; tone: string }) {
  if (!value) return null
  return (
    <span>
      <span className={cn('num font-semibold', tone)}>{value}</span> {label}
    </span>
  )
}

/** Waiting right now (it has a clock) wins over stopped earlier (it can be run again). */
function Captcha({ user }: { user: UserRow }) {
  if (user.liveWaiting > 0) {
    return (
      <StatusPill tone="warning" live size="sm">
        <span className="num">{user.liveWaiting}</span> waiting now
      </StatusPill>
    )
  }
  if (user.counts.captchaStopped > 0) {
    return (
      <StatusPill tone="danger" size="sm">
        <span className="num">{user.counts.captchaStopped}</span> stopped
      </StatusPill>
    )
  }
  return <span className="text-[13px] text-ink-faint">–</span>
}
